import React from "react";
import { useDropzone } from "react-dropzone";
import styles from "./myDropzone.scss";
import { ReactComponent as AddIcon } from "../icons/Add.svg";
import { ReactComponent as Border } from "../icons/border.svg";
import { METAVERSE_FOLDER, getMediaType, uploadXmlHTTP } from "../../utils/media-utils";
import ProgressBar from "../progress-bar";
import classNames from "classnames";
import configs from "../../utils/configs";
import { processThumbnail } from "../../utils/thumbnails";
import { getSignedUrl } from "../../utils/artwork-utils";
import { getAuthHeaders } from "../../utils/getToken";
import { uploadClaimFields } from "../../utils/upload-claim";

function MyDropzone({ onFileLoaded, register }) {
  const [progressLoading, setProgressLoading] = React.useState(0);
  const [acceptedFiles, setAcceptedFiles] = React.useState([]);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: async acceptedFiles => {
      const acceptedFile = acceptedFiles[0];

      // List of accepted file extensions
      const acceptedExtensions = ["glb", "png", "jpg", "jpeg", "gif", "pdf", "mp4", "mp3"];

      // Get the file extension
      const fileExtension = acceptedFile.name.split(".").pop().toLowerCase();

      // Check if the file extension is in the list of accepted extensions
      if (!acceptedExtensions.includes(fileExtension)) {
        alert("File type not supported. Only GLB, PNG, JPG, GIF, PDF, MP4 and MP3 files are allowed.");
        return;
      }

      // Check if the file size is more than 50MB
      if (acceptedFile.size > 50 * 1024 * 1024) {
        alert("File is too large. Maximum file size is 50MB.");
        return;
      }

      const blob = acceptedFile.slice(0, acceptedFile.size, acceptedFile.type);
      const newFile = new File([blob], acceptedFile.name, { type: acceptedFile.type });
      const onProgress = progress => {
        setProgressLoading(progress);
      };

      const fileResponse = await uploadXmlHTTP(newFile, acceptedFile?.type || "application/octet-stream", onProgress);

      // upload to my media
      const fileNameParts = acceptedFile.name.split(".");
      const extension = fileNameParts.pop();
      const name = fileNameParts.join(".");
      const fileName = `${name}.${extension}`;

      let thumbnail;
      let thumbnailSigned;

      try {
        const thumbnailFile = await processThumbnail(newFile, fileResponse?.download_url);

        thumbnailSigned = await getSignedUrl({
          file: thumbnailFile,
          folder: METAVERSE_FOLDER,
        });

        await fetch(thumbnailSigned.presigned_url, {
          method: "PUT",
          headers: {
            "Content-Type": "image/png",
            accept: "application/json",
            "x-ms-blob-type": "BlockBlob"
          },
          body: thumbnailFile
        });

        thumbnail = thumbnailSigned.download_url;
      } catch (error) {
        console.error("Error uploading thumbnail", error);
      }

      const entry = {
        name: fileName,
        title: fileName,
        path: fileResponse.key,
        type: getMediaType(extension),
        asset_type: "my-media",
        size: acceptedFile.size,
        source: "metaverse",
        ...uploadClaimFields(fileResponse, thumbnailSigned)
      };

      if (thumbnail) {
        entry.thumbnail = thumbnail;
      }

      const myMediaUrl = `${configs["PERSISTENCE_API_URL"]}/assets`;

      // send to my media
      const responseAssets = await fetch(myMediaUrl, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            accept: "application/json",
            ...(await getAuthHeaders(myMediaUrl, "POST"))
          },
        body: JSON.stringify(entry)
      });

      const responseAssetsJson = await responseAssets.json();
      acceptedFiles.downloableUrl = responseAssetsJson?.data?.url || fileResponse?.download_url;
      onFileLoaded(acceptedFiles);
      setAcceptedFiles(acceptedFiles);
    },
    accept: "model/gltf-binary, image/png, image/jpeg, image/gif, application/pdf, video/mp4, audio/mpeg"
  });

  return (
    <>
      <div className={classNames(styles.dropzoneContainer, progressLoading ? "events-none" : "")} {...getRootProps()}>
        <input {...getInputProps()} style={{ display: "none" }} type="file" />
        <div className={styles.dropzoneText}>
          <div className="create-tile-borders">
            <Border></Border>
          </div>
          {!progressLoading && (
            <div>
              <div className={styles.addIcon}>
                <AddIcon width={14} height={14} />
              </div>
              <p>Max file size 50MB</p>
              <strong>Drag and Drop</strong>
              <p>
                a file here or <b>browse</b>
              </p>
            </div>
          )}
          {progressLoading ? <ProgressBar className={styles.loader} progress={progressLoading}></ProgressBar> : ""}
        </div>
      </div>
    </>
  );
}

export default MyDropzone;
